import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@/utils/tailwind';

const buttonVariants = cva(
  `
    inline-flex items-center justify-center gap-[10px] whitespace-nowrap rounded-sm
     transition-colors duration-300 focus-visible:outline-none
    focus-visible:ring-1 focus-visible:ring-black
    disabled:pointer-events-none 
    [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0
  `,
  {
    variants: {
      variant: {
        // 실제 사용 하는 버튼 스타일////
        solid: 'bg-point-500 text-white  hover:bg-point-400 disabled:bg-[#EFEFEF]',
        secondary:
          'bg-gr-100 text-gr-700 fill-gr-700  hover:bg-gr-200 disabled:bg-gr-200 disabled:text-white fill:fill-gr-700 disabled:fill-gr-400',
        line: 'border border-gr-200 bg-white text-gr-700 fill-gr-700 hover:bg-gr-50 disabled:bg-gr-100 disabled:text-white disabled:fill-gr-400',
        ////////////////

        // 혹시 사용하는곳이 남아있을까 남겨둠 ///
        default: 'bg-point-500 text-white  hover:bg-point-400 disabled:bg-gr-200 ',
        muted: 'bg-gr-100 text-gr-700  hover:bg-[#F3F3F9]/90',
        destructive: 'bg-[#E02626] text-white  hover:bg-[#E02626]/90',
        outline: 'border border-gr-300 bg-white  hover:bg-gr-100 hover:text-gr-900',
        ghost: 'hover:bg-gr-100 hover:text-gr-900',
        link: 'text-[#FF7F00] underline-offset-4 hover:underline',
        /////////////
      },
      size: {
        // 실제 사용 하는 버튼 스타일////
        medium: 'px-2.5 h-11 Body5M14 gap-[10px]',
        small: 'px-[18px] py-[8px] Body5M14 gap-[10px]',
        icon: 'px-[7px] py-[7px] Body5M14',
        xsmall: 'px-[10px] py-[6px] Body5M14 h-10',
        ////////////////

        // 혹시 사용하는곳이 남아있을까 남겨둠 ///
        default: 'px-[18px] py-[8px] Body5M14 gap-[10px]',
        secondary: 'px-[10px] py-[6px] Body5M14',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',

        ////////////////
      },
    },
    defaultVariants: {
      variant: 'solid',
      size: 'medium',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingClassName?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, isLoading, variant, size, loadingClassName, asChild = false, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {isLoading ? (
          <span>
            <Loader2 className={cn(' w-[16px] animate-spin', loadingClassName)} />
          </span>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
